import type * as api from "./api";

const OWN = "(O)";
const VERBS: Record<string, readonly string[]> = { F: ["view", "create", "edit", "delete"], E: ["view", "edit"], C: ["view", "create"], V: ["view"] };
const LETTERS = ["V", "C", "E", "F"] as const;

const verbsOf = (letter: string | undefined) => (letter ? (VERBS[letter.replace(OWN, "")] ?? []) : []);

/** Rule U-12, as the API checks it: no verb and no reach that the base letter does not give. */
export function within(letter: string, base: string | undefined): boolean {
  if (!letter) return true;
  const verbs = verbsOf(letter);
  const baseVerbs = verbsOf(base);
  if (!verbs.length || !verbs.every((verb) => baseVerbs.includes(verb))) return false;
  return letter.endsWith(OWN) || !(base ?? "").endsWith(OWN);
}

/** The access choices of one module: none, then each letter up to the base letter. */
export function letterChoices(base: string | undefined): string[] {
  const all = LETTERS.flatMap((letter) => [letter, `${letter}${OWN}`]);
  return ["", ...all.filter((letter) => within(letter, base))];
}

const LETTER_TEXT: Record<string, string> = { F: "Full", E: "View and change", C: "View and add", V: "View" };

export function letterText(letter: string | undefined): string {
  if (!letter) return "None";
  const text = LETTER_TEXT[letter.replace(OWN, "")] ?? letter;
  return letter.endsWith(OWN) ? `${text} (own)` : text;
}

export const RESOURCE_TEXT: Record<string, string> = {
  tank: "Container levels",
  member: "Users and roles",
  account: "Accounts",
  pickup: "Pickups",
  area: "Areas",
  area_shape: "Area shapes",
  forecast: "Forecast",
  call: "Calls",
  issue: "Issues",
  issue_open: "Open an issue",
  container: "Containers",
  review: "Weekly check",
  load: "Loads",
  load_approve: "Approve a load",
  route: "Routes",
  scorecard: "Scorecard",
  prospect: "Leads",
  contract: "Contracts",
  activation: "Deals",
  activation_approve: "Approve a deal",
  lookup: "Settings lists",
  payout: "Rebates",
  payout_approve: "Approve rebates",
  route_run: "Route of the day",
  api_key: "API keys",
  bulk_edit: "Edit many accounts",
  visit: "Visits",
  pickup_request: "Pickup requests",
  fill_view: "Fill forecast",
  inventory: "Container units",
  inspection: "Pre-trip checks",
  tank_pin: "Container pins",
  calendar: "Cycle calendar",
  holiday: "Holidays",
  fleet: "Fleet",
  import: "Import",
  parity: "Sheet and app parity",
  cutover: "Cutover",
  alert: "Telegram alerts",
  portal_invite: "Customer portal invites",
  customer_message: "Customer messages",
  export: "Exports",
  activity: "Activity",
  cutover_finish: "Finish the cutover",
  visit_label: "Visit labels",
};

export const resourceText = (resource: string) => RESOURCE_TEXT[resource] ?? resource.replace(/_/g, " ");

/** Rules U-11, U-13, T-14 and T-18: only the built-in administrator role manages these modules. */
export const BUILT_IN_ONLY: readonly string[] = ["member", "api_key", "import", "alert", "activity", "cutover_finish"];

/** An older API names the import module sheet_import (roadmap 091) and the cutover module area_source (085). */
const RENAMED: Record<string, string> = { sheet_import: "import", area_source: "cutover" };

export const renamedModules = (list: string[]): string[] => [
  ...list,
  ...Object.entries(RENAMED)
    .filter(([old, name]) => list.includes(old) && !list.includes(name))
    .map(([, name]) => name),
];

/** A copy of the administrator never gets the users, the roles or the API keys (U-12). */
export function sameAccessText(basedOn: string, baseName: string): string {
  const without = basedOn === "administrator" ? ", without users, roles and API keys" : "";
  return `The same access as ${baseName}${without}.`;
}

/** The letters that a custom role can hold: the built-in-only modules go. */
export function customLetters(letters: api.Letters): api.Letters {
  return Object.fromEntries(Object.entries(letters).filter(([resource]) => !BUILT_IN_ONLY.includes(resource)));
}

/** The modules where the custom role gives less than its base role. */
export function changedFrom(letters: api.Letters, base: api.Letters, resources: readonly string[]): string[] {
  return resources.filter((resource) => !BUILT_IN_ONLY.includes(resource) && (letters[resource] ?? "") !== (base[resource] ?? ""));
}

export function nameProblem(name: string, taken: readonly string[]): string | null {
  const clean = name.trim();
  if (!clean) return "Enter a name.";
  if (clean.length > 60) return "Use 60 characters or fewer.";
  if (taken.some((other) => other.toLowerCase() === clean.toLowerCase())) return "A role has this name. Use a different name.";
  return null;
}

export interface RoleRow {
  key: string;
  id: string | null;
  name: string;
  kind: "built-in" | "custom";
  base: string;
  modules: number;
  assigned: number | null;
  created_at: string | null;
}

/** One table row for each role: the built-in roles, then the custom roles. */
export function roleRows(list: api.RoleList, names: (role: string) => string): RoleRow[] {
  const count = (letters: api.Letters) => Object.values(letters).filter(Boolean).length;
  return [
    ...list.built_in.map((role) => ({ key: role.key, id: null, name: names(role.key), kind: "built-in" as const, base: "", modules: count(role.letters), assigned: null, created_at: null })),
    ...list.custom.map((role) => ({ key: role.key, id: role.id, name: role.name, kind: "custom" as const, base: names(role.based_on), modules: count(role.letters), assigned: role.assigned, created_at: role.created_at })),
  ];
}

export interface MatrixColumn {
  key: string;
  id: string | null;
  name: string;
  kind: "built-in" | "custom";
  letters: api.Letters;
  members: number;
}

/** One matrix column for each role, built-in first; the active members that hold it at any place. */
export function matrixColumns(list: api.RoleList, members: readonly api.Member[], names: (role: string) => string): MatrixColumn[] {
  const holders = (role: string) => members.filter((m) => m.status === "active" && m.roles.some((grant) => grant.role === role)).length;
  return [
    ...list.built_in.map((role) => ({ key: role.key, id: null, name: names(role.key), kind: "built-in" as const, letters: role.letters, members: holders(role.key) })),
    ...list.custom.map((role) => ({ key: role.key, id: role.id, name: role.name, kind: "custom" as const, letters: role.letters, members: holders(role.key) })),
  ];
}

/** The plain words of each matrix letter, from the verbs of the roles catalogue. */
export const LETTER_LEGEND: readonly (readonly [string, string])[] = [
  ["F", "Full: view, add, change and remove"],
  ["E", "View and change"],
  ["C", "View and add"],
  ["V", "View only"],
  [OWN, "Only the records of the member: the accounts, containers, leads, issues, loads and routes that are assigned to the member"],
  ["–", "No access"],
];

export const matrixLetter = (letter: string | undefined) => letter || "–";
